import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Spinner, Alert, Button } from 'react-bootstrap';
import { BiCaretUp, BiCaretDown } from 'react-icons/bi';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { usePeriodBuffer } from '../../context/PeriodBufferContext';

// ---------- Local helpers (mirrors of the ones in InventoryDetail) ----------

const MIN_STEPS_PER_BOX = 10;
const WEIGHT_REGEX = /([\d.,]+)\s*([a-zA-Z]*)/;

function parseWeightToBaseUnits(raw) {
  if (!raw || typeof raw !== 'string') return { value: 0, unitLabel: 'units' };
  const m = raw.trim().toLowerCase().match(WEIGHT_REGEX);
  if (!m) return { value: 0, unitLabel: 'units' };
  const num = parseFloat(String(m[1]).replace(',', '.'));
  const unit = (m[2] || '').toLowerCase();
  if (unit.startsWith('kg')) return { value: Math.round(num * 1000), unitLabel: 'g' };
  if (unit === 'g' || unit.startsWith('gram')) return { value: Math.round(num), unitLabel: 'g' };
  if (unit === 'l' || unit === 'lt' || unit.startsWith('lit')) return { value: Math.round(num * 1000), unitLabel: 'ml' };
  if (unit === 'ml') return { value: Math.round(num), unitLabel: 'ml' };
  if (raw.toLowerCase().includes('ml')) return { value: Math.round(num), unitLabel: 'ml' };
  if (raw.toLowerCase().includes('g')) return { value: Math.round(num), unitLabel: 'g' };
  return { value: Math.round(num), unitLabel: 'units' };
}

function parseFlexibleNumber(raw) {
  if (typeof raw === 'number' && Number.isFinite(raw)) return raw;
  if (raw == null) return 0;
  let s = String(raw).trim().replace(/\s+/g, '');
  if (s.includes(',') && s.includes('.')) s = s.replace(/\./g, '').replace(',', '.');
  else if (s.includes(',')) s = s.replace(',', '.');
  const n = parseFloat(s);
  return Number.isFinite(n) ? n : 0;
}

function normalizeUnit(u) {
  const s = String(u || '').trim().toLowerCase();
  if (['kilogram', 'kilograms', 'kg'].includes(s)) return 'kg';
  if (['gram', 'grams', 'g'].includes(s)) return 'g';
  if (['liter', 'litre', 'liters', 'litres', 'l'].includes(s)) return 'l';
  if (['milliliter', 'millilitre', 'milliliters', 'millilitres', 'ml'].includes(s)) return 'ml';
  if (['unit', 'units', 'each'].includes(s)) return 'units';
  return s || 'units';
}

function convertUnits(value, fromUnit, toUnit) {
  const fromU = normalizeUnit(fromUnit);
  const toU = normalizeUnit(toUnit);
  if (fromU === toU) return value;
  if (fromU === 'kg' && toU === 'g') return value * 1000;
  if (fromU === 'g' && toU === 'kg') return value / 1000;
  if (fromU === 'l' && toU === 'ml') return value * 1000;
  if (fromU === 'ml' && toU === 'l') return value / 1000;
  return value;
}

const clamp = (v, min, max) => Math.max(min, Math.min(max, v));

// ---------- Component ----------

export default function InventoryProductTile({ product, category, onUpdateSuccess }) {
  const navigate = useNavigate();
  const { isAuthenticated, verifySession, logout, user } = useAuth();
  const { periodBuffers } = usePeriodBuffer();

  const [flipped, setFlipped] = useState(false);
  // Ref to the outer .flip-tile element so the document-level click handler
  // can tell whether a click was inside or outside this tile.
  const tileRef = useRef(null);
  const [loaded, setLoaded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [error, setError] = useState('');

  // Mirror of the relevant subset of InventoryDetail state
  const [stockCount, setStockCount] = useState(0);
  const [containerBoxes, setContainerBoxes] = useState([]);
  const [measureIncrement, setMeasureIncrement] = useState(0);
  const [measureMaxVal, setMeasureMaxVal] = useState(0);
  const [unitLabel, setUnitLabel] = useState('units');
  const [showCount, setShowCount] = useState(false);
  const [showBox, setShowBox] = useState(false);
  const [boxCount, setBoxCount] = useState(0);

  // `lastStockTake` is set during load + update so future renders use a fresh
  // reference, but the value itself is only consumed via `isWithinPeriod`.
  const [, setLastStockTake] = useState(null);
  const [isWithinPeriod, setIsWithinPeriod] = useState(false);

  // Admin override — Master Admin (userGroup 0) or Tenant Admin (userGroup 1)
  // can bypass the stock-take lock. Tenant isolation is enforced server-side,
  // so a Tenant Admin can only ever override their own tenant's items.
  const [isOverride, setIsOverride] = useState(false);
  const effectiveUserGroup = user?.userGroup ?? user?.group ?? null;
  const isAdmin =
    Number(effectiveUserGroup) === 0 || Number(effectiveUserGroup) === 1;

  // Audit metadata
  const [uomIdState, setUomIdState] = useState(null);
  const [uomLabelState, setUomLabelState] = useState(null);
  const [containerTypeIdState, setContainerTypeIdState] = useState(null);
  const [containerTypeDescState, setContainerTypeDescState] = useState(null);

  const lockEditing = isWithinPeriod && !isOverride;

  const stepsPerBox = useMemo(() => {
    const inc = measureIncrement > 0 ? measureIncrement : 1;
    const max = measureMaxVal > 0 ? measureMaxVal : inc;
    return Math.max(1, Math.ceil(max / inc));
  }, [measureIncrement, measureMaxVal]);

  const computedQuantity = useMemo(() => {
    if (showCount) return Number.isFinite(stockCount) ? stockCount : 0;
    if (showBox && containerBoxes.length) {
      const inc = measureIncrement > 0 ? measureIncrement : 1;
      return containerBoxes.reduce((sum, steps) => sum + steps * inc, 0);
    }
    return 0;
  }, [showCount, showBox, containerBoxes, stockCount, measureIncrement]);

  // Aggregated visuals — one virtual container with combined capacity.
  const aggregatedCapacity = (measureMaxVal || 0) * (boxCount || 0);
  const aggregatedTotalSteps = stepsPerBox * (boxCount || 0);
  const aggregatedFilledSteps = containerBoxes.reduce((a, b) => a + b, 0);
  const aggregatedFilledAmount =
    aggregatedFilledSteps * (measureIncrement > 0 ? measureIncrement : 1);
  const fillPct =
    aggregatedTotalSteps > 0
      ? Math.max(0, Math.min(1, aggregatedFilledSteps / aggregatedTotalSteps))
      : 0;

  // ---------- Data load (lazy on first flip) ----------

  const loadBackFaceData = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      if (!isAuthenticated || !product?.id) return;
      await verifySession();

      // Product details (only need metadata.weight; name + image come from props)
      const prodRes = await axios.post(
        '/products/by-id',
        { id: product.id },
        { withCredentials: true }
      );
      const prod = prodRes?.data ?? {};
      const { value: weightValue, unitLabel: parsedUnit } = parseWeightToBaseUnits(
        prod?.metadata?.weight || ''
      );

      // UOM / Container Type
      const uomRes = await axios.get(`/uom-inventory/by-product/${product.id}`, {
        withCredentials: true,
      });
      const uomList = uomRes?.data?.data ?? uomRes?.data ?? [];
      const hasUom = Array.isArray(uomList) && uomList.length > 0;

      let nextShowCount = false;
      let nextShowBox = false;
      let nextStockCount = 0;
      let nextMeasureInc = 0;
      let nextMeasureMax = 0;
      let nextBoxCount = 0;
      let nextUnitLabel = parsedUnit || 'ml';

      let nextUomId = null;
      let nextUomLabel = null;
      let nextContainerTypeId = null;
      let nextContainerTypeDesc = null;

      const hasWord = (rx, ...vals) => vals.some((v) => rx.test(String(v || '')));
      const endsWithSingleG = (s) => /(?:^|[^A-Za-z])g$/i.test(String(s || '').trim());

      if (hasUom) {
        const inv = uomList[0];
        nextUomId = inv?.uomId || null;
        nextUomLabel = String(
          inv?.uomFriendlyName || inv?.uomName || inv?.uomLabel || inv?.uomDescription || ''
        ) || null;
        const maxQuantity = inv?.maxQuantity;
        nextContainerTypeId = inv?.containerTypeId || null;

        if (nextUomId === '04e88a70-8929-4948-8632-2859dc984cec') {
          // Each
          nextShowCount = true;
          nextStockCount = Math.max(1, Math.floor(Number(maxQuantity) || 1));
          nextUnitLabel = 'units';
        } else if (nextContainerTypeId) {
          const ctRes = await axios.get(`/container-types/${nextContainerTypeId}`, {
            withCredentials: true,
          });
          const ct = ctRes?.data?.data ?? ctRes?.data ?? {};
          const ctName = String(ct?.containerTypeName || '');
          const ctFriendly = String(ct?.friendlyName || '').trim();
          nextContainerTypeDesc = ctName || null;

          const isGramCT = endsWithSingleG(ctFriendly);
          const forceBoxLike = isGramCT || hasWord(/box|pack/i, ctName, ctFriendly, nextUomLabel);
          const isBottle = !forceBoxLike && hasWord(/bottle/i, ctName, ctFriendly, nextUomLabel);
          const isSack = !forceBoxLike && hasWord(/sack/i, ctName, ctFriendly, nextUomLabel);

          if (forceBoxLike || isBottle || isSack) {
            nextShowBox = true;
            const productBasedMax =
              Number.isFinite(weightValue) && weightValue > 0 ? weightValue : 0;
            let maxNum = 0;
            if (productBasedMax > 0) {
              maxNum = productBasedMax;
              nextUnitLabel = isGramCT ? 'g' : parsedUnit || 'g';
            } else if (Number(ct?.measureMax) > 0) {
              maxNum = Number(ct.measureMax);
              nextUnitLabel = isGramCT
                ? 'g'
                : parsedUnit && parsedUnit !== 'units'
                ? parsedUnit
                : 'g';
            } else {
              maxNum = 500;
              nextUnitLabel = isGramCT
                ? 'g'
                : parsedUnit && parsedUnit !== 'units'
                ? parsedUnit
                : 'g';
            }
            const ctInc = Number(ct?.measureIncrement) > 0 ? Number(ct.measureIncrement) : 0;
            const fallbackInc = Math.max(1, Math.floor(maxNum / MIN_STEPS_PER_BOX));
            nextMeasureMax = maxNum;
            nextMeasureInc = ctInc > 0 ? ctInc : fallbackInc;
            nextBoxCount = Math.max(1, Math.floor(Number(maxQuantity) || 1));
          } else {
            nextShowCount = true;
            nextStockCount = Math.max(1, Math.floor(Number(maxQuantity) || 1));
            nextUnitLabel =
              nextUomLabel || (parsedUnit && parsedUnit !== 'units' ? parsedUnit : 'units');
          }
        } else {
          const boxishFromUom = /box|pack/i.test(nextUomLabel || '');
          const isSack = /sack/i.test(nextUomLabel || '');
          if (boxishFromUom || isSack) {
            nextShowBox = true;
            const productBasedMax =
              Number.isFinite(weightValue) && weightValue > 0 ? weightValue : 0;
            const maxNum = productBasedMax > 0 ? productBasedMax : 500;
            nextMeasureMax = maxNum;
            nextMeasureInc = Math.max(1, Math.floor(maxNum / MIN_STEPS_PER_BOX));
            nextBoxCount = Math.max(1, Math.floor(Number(inv?.maxQuantity) || 1));
            nextUnitLabel = parsedUnit && parsedUnit !== 'units' ? parsedUnit : 'g';
          } else {
            nextShowCount = true;
            nextStockCount = Math.max(1, Math.floor(Number(inv?.maxQuantity) || 1));
            nextUnitLabel =
              nextUomLabel || (parsedUnit && parsedUnit !== 'units' ? parsedUnit : 'units');
          }
        }
      }

      // Product-Inventory (prefill + lock-state)
      let invData = null;
      try {
        const piRes = await axios.get(`/product-inventory/product/${product.id}`, {
          withCredentials: true,
        });
        invData = piRes?.data?.data ?? null;
      } catch (e) {
        if (e?.response?.status !== 404) throw e;
      }

      if (nextShowCount && !nextShowBox) {
        let storedCount = invData
          ? parseFlexibleNumber(
              typeof invData.quantity !== 'undefined' && invData.quantity !== null
                ? invData.quantity
                : invData.stockTakeCount
            )
          : 0;
        const storedUnit =
          invData && typeof invData.quantity === 'undefined' ? invData.stockTakeUnit : undefined;
        if (storedUnit) storedCount = convertUnits(storedCount, storedUnit, nextUnitLabel);
        nextStockCount = Math.max(0, Math.round(storedCount));
      }

      let nextLastTake = null;
      let nextIsWithinPeriod = false;
      if (invData) {
        nextLastTake = invData.lastStockTake ? new Date(invData.lastStockTake) : null;
        const periodDays =
          typeof invData.inventoryPeriod === 'number' ? invData.inventoryPeriod : 0;
        let bufferHours = 0;
        if (periodDays > 0) {
          try {
            const periodsRes = await axios.get('/inventory-periods', { withCredentials: true });
            const periods = periodsRes.data?.data || periodsRes.data || [];
            const period = periods.find((p) => p.inventoryDays === periodDays);
            if (period) bufferHours = periodBuffers.get(period.id) || 0;
          } catch (err) {
            // fall through — bufferHours stays 0
          }
        }
        if (nextLastTake && Number.isFinite(periodDays) && periodDays > 0) {
          const periodInMs = periodDays * 24 * 60 * 60 * 1000;
          const bufferInMs = bufferHours * 60 * 60 * 1000;
          const adjustedPeriodInMs = periodInMs - bufferInMs;
          const timeSinceLastTake = Date.now() - nextLastTake.getTime();
          nextIsWithinPeriod = timeSinceLastTake < adjustedPeriodInMs;
        }
      }

      // Prefill containers from stored quantity (mirrors InventoryDetail logic)
      let prefilledBoxes = [];
      if (nextShowBox && nextBoxCount > 0) {
        const inc = nextMeasureInc > 0 ? nextMeasureInc : 1;
        const maxPerBox = nextMeasureMax > 0 ? nextMeasureMax : inc;
        const maxSteps = Math.max(1, Math.ceil(maxPerBox / inc));
        let storedCount = invData
          ? parseFlexibleNumber(
              typeof invData.quantity !== 'undefined' && invData.quantity !== null
                ? invData.quantity
                : invData.stockTakeCount
            )
          : 0;
        const storedUnit =
          invData && typeof invData.quantity === 'undefined' ? invData.stockTakeUnit : undefined;
        if (storedUnit) storedCount = convertUnits(storedCount, storedUnit, nextUnitLabel);
        let remaining = clamp(storedCount, 0, maxPerBox * nextBoxCount);
        for (let i = 0; i < nextBoxCount; i++) {
          const stepsForThis = Math.min(Math.floor(remaining / inc), maxSteps);
          prefilledBoxes.push(stepsForThis);
          remaining -= stepsForThis * inc;
        }
      }

      // Commit all state at once
      setShowCount(nextShowCount);
      setShowBox(nextShowBox);
      setStockCount(nextStockCount);
      setMeasureIncrement(nextMeasureInc);
      setMeasureMaxVal(nextMeasureMax);
      setBoxCount(nextBoxCount);
      setUnitLabel(nextUnitLabel);
      setContainerBoxes(
        nextShowBox
          ? prefilledBoxes.length
            ? prefilledBoxes
            : Array.from({ length: nextBoxCount }, () => 0)
          : []
      );
      setLastStockTake(nextLastTake);
      setIsWithinPeriod(nextIsWithinPeriod);
      setUomIdState(nextUomId);
      setUomLabelState(nextUomLabel);
      setContainerTypeIdState(nextContainerTypeId);
      setContainerTypeDescState(nextContainerTypeDesc);

      setLoaded(true);
    } catch (err) {
      console.error('Flip-tile load failed:', err);
      if (err.response?.status === 401) {
        logout();
        return;
      }
      setError(err.response?.data?.message || 'Failed to load product stock');
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, product?.id, verifySession, periodBuffers, logout]);

  // Flip handler — lazy-load data on first flip.
  const handleTileClick = () => {
    if (!flipped && !loaded && !loading) {
      loadBackFaceData();
    }
    setFlipped((v) => !v);
  };

  // Click-outside: when the back face is showing, a mousedown anywhere outside
  // this tile flips it back to the front. We attach to `mousedown` (not
  // `click`) so the unflip happens before any other element's click handler
  // can navigate / submit. Mid-update is excluded so the user can't cancel a
  // PATCH-in-flight by clicking off-tile.
  useEffect(() => {
    if (!flipped) return undefined;
    const handleDocMouseDown = (event) => {
      if (updating) return;
      const node = tileRef.current;
      if (node && !node.contains(event.target)) {
        setFlipped(false);
      }
    };
    document.addEventListener('mousedown', handleDocMouseDown);
    return () => {
      document.removeEventListener('mousedown', handleDocMouseDown);
    };
  }, [flipped, updating]);

  // ---------- Step handlers (back face arrows) ----------

  const incTotal = (e) => {
    e?.stopPropagation();
    if (lockEditing || updating) return;
    if (showCount) {
      setStockCount((c) => c + 1);
      return;
    }
    // Box mode: increment the first non-full box (preserves "fill left-to-right" UX).
    setContainerBoxes((boxes) => {
      const idx = boxes.findIndex((s) => s < stepsPerBox);
      if (idx === -1) return boxes;
      return boxes.map((s, i) => (i === idx ? s + 1 : s));
    });
  };

  const decTotal = (e) => {
    e?.stopPropagation();
    if (lockEditing || updating) return;
    if (showCount) {
      setStockCount((c) => Math.max(0, c - 1));
      return;
    }
    // Box mode: decrement the last non-zero box.
    setContainerBoxes((boxes) => {
      for (let i = boxes.length - 1; i >= 0; i--) {
        if (boxes[i] > 0) {
          return boxes.map((s, j) => (j === i ? s - 1 : s));
        }
      }
      return boxes;
    });
  };

  // ---------- Show Detail / Update ----------

  const handleShowDetailClick = (e) => {
    e.stopPropagation();
    navigate(`/dashboard/inventory/${product.id}`, {
      state: { returnCategory: category },
    });
  };

  const handleUpdateClick = async (e) => {
    e.stopPropagation();
    if (lockEditing || updating || !loaded) return;
    try {
      setUpdating(true);
      setError('');
      await verifySession();

      // PATCH product-inventory
      const nowISO = new Date().toISOString();
      await axios.patch(
        `/product-inventory/product/${product.id}`,
        { lastStockTake: nowISO, quantity: computedQuantity },
        { withCredentials: true }
      );

      // Re-fetch for audit source-of-truth
      const [invLatestRes, uomInvRes] = await Promise.all([
        axios.get(`/product-inventory/product/${product.id}`, { withCredentials: true }),
        axios.get(`/uom-inventory/by-product/${product.id}`, { withCredentials: true }),
      ]);
      const invLatest = invLatestRes?.data?.data ?? {};
      const uomListNow = uomInvRes?.data?.data ?? uomInvRes?.data ?? [];
      const firstUom = Array.isArray(uomListNow) && uomListNow.length ? uomListNow[0] : null;
      const uomId = uomIdState || firstUom?.uomId || null;
      const containerTypeId = containerTypeIdState || firstUom?.containerTypeId || null;

      // Resolve labels if missing
      let uomLabel = uomLabelState;
      if (!uomLabel && uomId) {
        try {
          const r = await axios.get(`/uoms/${uomId}`, { withCredentials: true });
          uomLabel = r?.data?.data?.label ?? r?.data?.label ?? null;
        } catch {
          uomLabel = null;
        }
      }

      let containerTypeDesc = containerTypeDescState;
      let ctChosen = null;
      if (containerTypeId) {
        try {
          const r = await axios.get(`/container-types/${containerTypeId}`, {
            withCredentials: true,
          });
          ctChosen = r?.data?.data ?? r?.data ?? null;
          containerTypeDesc = ctChosen?.containerTypeName || containerTypeDesc;
        } catch {
          /* ignore */
        }
      }

      const lowLevelWarning1Enable = Boolean(ctChosen?.lowLevelWarning1Enable);
      const lowLevelWarning2Enable = Boolean(ctChosen?.lowLevelWarning2Enable);
      const lowLevelWarning3Enable = Boolean(ctChosen?.lowLevelWarning3Enable);
      const lowLevel1Warning =
        ctChosen?.lowLevel1Warning != null ? parseFlexibleNumber(ctChosen.lowLevel1Warning) : null;
      const lowLevel2Warning =
        ctChosen?.lowLevel2Warning != null ? parseFlexibleNumber(ctChosen.lowLevel2Warning) : null;
      const lowLevel3Warning =
        ctChosen?.lowLevel3Warning != null ? parseFlexibleNumber(ctChosen.lowLevel3Warning) : null;

      const auditPayload = {
        inventory_id: invLatest?.id ?? invLatest?._id ?? `temp-${Date.now()}`,
        product_id: invLatest?.productUuid ?? product.id,
        product_name: (product?.name || '').toString().trim(),
        uomId,
        uomLabel: uomLabel || 'Unknown',
        containerTypeId,
        containerTypeDesc: containerTypeDesc || 'Unknown',
        lowLevelWarning1Enable,
        lowLevelWarning2Enable,
        lowLevelWarning3Enable,
        lowLevel1Warning,
        lowLevel2Warning,
        lowLevel3Warning,
        lastStockTake: invLatest?.lastStockTake ?? nowISO,
        quantity: invLatest?.quantity ?? computedQuantity,
        inventoryPeriod: invLatest?.inventoryPeriod ?? 0,
        createdBy: user?.id || user?._id || 'unknown-user',
        action_type: 'UPDATE',
        changed_by: user?.id || user?._id || 'unknown-user',
        change_reason: 'Quick stock-take update (flip-tile)',
      };
      await axios.post('/audit-inventory', auditPayload, { withCredentials: true });

      // Update local lastStockTake so the lock-editing window starts now.
      setLastStockTake(new Date(invLatest?.lastStockTake ?? nowISO));

      // Auto-flip-back + parent toast
      setFlipped(false);
      if (typeof onUpdateSuccess === 'function') {
        onUpdateSuccess(product?.id, product?.name);
      }
    } catch (err) {
      console.error('Flip-tile update failed:', err);
      if (err.response?.status === 401) {
        logout();
        return;
      }
      setError(err.response?.data?.message || 'Update failed');
    } finally {
      setUpdating(false);
    }
  };

  // ---------- Render helpers ----------

  // Format aggregated amount with the unit label.
  const fmt = (n) => `${Number(n || 0).toFixed(0)}${unitLabel}`;

  const renderBackBody = () => {
    if (loading) {
      return (
        <div className="d-flex justify-content-center align-items-center h-100">
          <Spinner animation="border" size="sm" />
        </div>
      );
    }
    if (error) {
      return (
        <Alert variant="danger" className="mb-0 p-2 small">
          {error}
        </Alert>
      );
    }
    if (!loaded) return null;

    return (
      <div className="d-flex flex-column align-items-center justify-content-center h-100 px-2">
        <div className="flip-tile-front-title text-center mb-1">
          {product?.name}
        </div>
        <div className="flip-tile-label text-center mb-2">
          Stock Take Count ({unitLabel})
        </div>
        <div className="flip-tile-value mb-2">
          {showCount ? stockCount : fmt(aggregatedFilledAmount)}
        </div>

        {showBox && boxCount > 0 && (
          <div
            className="flip-tile-aggregated-box mb-2"
            title={`${fmt(aggregatedFilledAmount)} / ${fmt(aggregatedCapacity)}`}
          >
            <div
              className="flip-tile-aggregated-fill"
              style={{ width: `${(fillPct * 100).toFixed(1)}%` }}
            />
            <span className="flip-tile-aggregated-text">
              {fmt(aggregatedFilledAmount)} / {fmt(aggregatedCapacity)}
            </span>
          </div>
        )}

        <div className="flip-tile-arrows" onClick={(e) => e.stopPropagation()}>
          <button
            type="button"
            className="flip-tile-arrow"
            onClick={incTotal}
            disabled={lockEditing || updating}
            aria-label="Increase"
          >
            <BiCaretUp />
          </button>
          <button
            type="button"
            className="flip-tile-arrow"
            onClick={decTotal}
            disabled={lockEditing || updating}
            aria-label="Decrease"
          >
            <BiCaretDown />
          </button>
        </div>

        {lockEditing && (
          <div className="flip-tile-locked-hint mt-1">
            Locked — within stock-take period
          </div>
        )}

        {isWithinPeriod && isAdmin && !isOverride && (
          <Button
            size="sm"
            variant="warning"
            className="mt-1"
            onClick={(e) => {
              e.stopPropagation();
              setIsOverride(true);
            }}
            disabled={updating}
            title="Bypass the stock-take lock (admin only)"
          >
            Override Lock
          </Button>
        )}

        {isWithinPeriod && isOverride && (
          <div className="flip-tile-locked-hint mt-1 text-muted">
            Admin override active
          </div>
        )}
      </div>
    );
  };

  return (
    <div
      ref={tileRef}
      className={`flip-tile ${flipped ? 'flipped' : ''}`}
      onClick={handleTileClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleTileClick();
        }
      }}
    >
      <div className="flip-tile-inner">
        {/* ----- Front face ----- */}
        {/* Plain divs (not Bootstrap <Card>) — Bootstrap's `.card` applies
            position: relative + display: flex which interfered with the
            absolutely-positioned 3D face. The styling is provided by
            .flip-tile-face / .flip-tile-card-body in App.css. */}
        <div className="flip-tile-face flip-tile-front">
          <div className="flip-tile-card-body flip-tile-front-body">
            <div className="flip-tile-front-title">{product.name}</div>
            <div className="product-image-container">
              <img
                src={product.imageURL || '/placeholder-product.png'}
                alt={product.name}
                className="product-image img-fluid"
                style={{ maxHeight: '140px', maxWidth: '100%' }}
              />
            </div>
          </div>
        </div>

        {/* ----- Back face ----- */}
        <div className="flip-tile-face flip-tile-back">
          <button
            type="button"
            className="flip-tile-corner flip-tile-corner-left"
            onClick={handleShowDetailClick}
            disabled={updating}
          >
            Show Detail
          </button>
          <button
            type="button"
            className="flip-tile-corner flip-tile-corner-right"
            onClick={handleUpdateClick}
            disabled={lockEditing || updating || !loaded}
          >
            {updating ? '…' : 'Update'}
          </button>
          <div className="flip-tile-card-body flip-tile-back-body">
            {renderBackBody()}
          </div>
        </div>
      </div>
    </div>
  );
}
